import { useMemo, useEffect, useCallback, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useChat } from '@ai-sdk/react';
import { createDesktopChatAcceptance } from '~/features/chat/desktop-composer-bridge';
import { DefaultChatTransport } from 'ai';
import type { UIMessage } from 'ai';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { chatKeys, folderKeys, planKeys } from '~/lib/query-keys';
import { chatQueryKeysForToolResult } from '~/lib/chat-tool-invalidation';
import { timezoneHeader } from '~/lib/timezone';
import { useSession } from '~/lib/stores/session-store';
import { useBranches, buildForkGroups, nextActiveBranch } from '~/hooks/queries/use-branches';
import { reconcileHistory } from '~/hooks/reconcile-history';
import { resumeTargetToSuppress } from '~/hooks/resume-decision';
import { ResumePursuitController, isResumeRequestUrl } from '~/hooks/resume-pursuit-controller';
import { refreshActiveChatModel, resolveTurnModelMode } from '~/hooks/queries/use-model-picker';
import { useCancelBackgroundChat } from '~/hooks/mutations/use-cancel-background-chat';
import { extractBackgroundChatRunSignal, resolveStopRunId } from '~/hooks/background-chat-stop';
import {
  claimLocalHistoryWrite,
  handOffAfterStreamEnd,
  releaseLocalHistoryWrite,
} from '~/hooks/local-history-writer';
import type { AssetEditablePayload } from '~/features/chat/editable-asset';
import { markStoppedTail } from '~/features/chat/stopped-turn';
import { rerunPickupOnTurnEnd, turnEndedHere } from '~/features/chat/late-continuation-pickup';

interface RetryResponse {
  branchId: string;
  branchedFromMessageId: number;
  sourceBranchId: string;
  content: string;
}

/**
 * FRONT-1567: mirrors `ChatAttachment` (chat-input.tsx) — kept as this
 * hook's own local type rather than importing that one, but the wire shape
 * (and the `source` meaning: absent/"upload" vs. "document", where `fileId`
 * then carries a document filename) must stay identical.
 */
interface AttachmentRef {
  fileId: string;
  name: string;
  contentType: string;
  kind: string;
  source?: 'upload' | 'document';
}

/**
 * Button-origin marker for a user message (FRONT-199). Set by the inbox
 * modal's resolve buttons so the bubble can render a friendly status chip
 * ("Updating document…") instead of the raw instruction the button sends as
 * the message content. Display-only: the LLM still receives the full prompt.
 */
export interface MessageSource {
  kind: 'button';
  action: string;
  label: string;
}

/** Extra, one-shot request fields a chat surface's `onSendMessage` may carry. */
export interface ChatSendOptions {
  /** FRONT-2555: the composer's queue sent this on its own (see BranchTurnState). */
  queuedSend?: boolean;
}

/** The `onSendMessage` shape threaded from a chat mount into its cards. */
export type ChatSendMessage = (text: string, options?: ChatSendOptions) => void | Promise<void>;

interface BranchTurnState {
  branchId: string;
  branchedFromMessageId?: number;
  attachments?: AttachmentRef[];
  /**
   * One-shot flag (FRONT-2555): the composer's queue fired this message on
   * its own once the previous turn ended. It was typed before whatever that
   * turn raised, so `body.queuedSend` tells the chat-proxy it never answers
   * an approval card. Consumed like the other one-shot fields.
   */
  queuedSend?: boolean;
  /**
   * One-shot flag (FRONT-201) set by the inbox chat modal's resolve
   * buttons (Save to Graph / Save as document / Update document). The
   * transport body lifts it to `body.inlineOnly = true` for the next turn
   * so the chat-proxy keeps the action inline in the modal's own chat —
   * propose_plan is refused that turn, so the work can't be punted to a
   * background plan that would surface in the main chat. Consumed and
   * cleared inside the body fn like the other one-shot fields here.
   */
  inlineOnly?: boolean;
  /**
   * One-shot button-origin marker (FRONT-199) set by the inbox modal's
   * resolve buttons. The transport body lifts it to `body.source` so the
   * backend persists it on the user row; the bubble then renders a friendly
   * status chip instead of the raw prompt. Consumed and cleared inside the
   * body fn like the other one-shot fields here.
   */
  source?: MessageSource;
  /**
   * One-shot target filename (FRONT-221) set by the inbox modal's "Update
   * document" button. The transport body lifts it to `body.saveReplyToDocument`
   * so the chat-proxy, when the turn finalizes server-side, writes the
   * assistant's reply back into this document — making the save complete in the
   * background even if the modal was closed. Consumed and cleared inside the
   * body fn like the other one-shot fields here.
   */
  saveReplyToDocument?: string;
  /**
   * One-shot directive (FRONT-269) set by the inbox modal's "Update document"
   * button. Lifted to `body.updateDocument = true`; the chat-proxy maps it to
   * the canonical rewrite instruction server-side (and forces the turn inline),
   * so the button no longer injects an English prompt for the orchestrator to
   * interpret. Consumed and cleared inside the body fn.
   */
  updateDocument?: boolean;
  /**
   * One-shot directive (FRONT-269) set by the inbox modal's "Save to Graph"
   * button. Lifted to `body.writeToGraph = true`; the chat-proxy triggers the
   * write-to-graph preview/approval gate directly instead of relying on a
   * typed `/write-to-graph` slash command. Consumed and cleared inside the
   * body fn.
   */
  writeToGraph?: boolean;
  /**
   * One-shot flags (FRONT-535) set by the composer's image button + model
   * picker. Lifted to `body.imageIntent` / `body.imageModel`; the chat-proxy
   * nudges the orchestrator to call generate_image and threads the chosen model
   * to the tool. Consumed and cleared inside the body fn like the others.
   */
  imageIntent?: boolean;
  imageModel?: string;
}

/**
 * Hosts a chat instance for one session. Defaults to the user's global
 * session (the main inbox/chat surface). Pass `sessionIdOverride` to bind a
 * second, isolated instance to a different conversation — e.g. the per-asset
 * inbox chat modal, which uses `inbox-<itemId>`. Everything in this hook is
 * derived from the single `sessionId` below, so two instances with distinct
 * ids (the AI SDK keys its in-memory thread on `chat-${sessionId}`) never
 * cross-talk, and the override path needs no other change.
 */
export function useAgentChat(
  sessionIdOverride?: string,
  opts?: {
    assetContext?: string;
    assetEditable?: AssetEditablePayload;
    scopeEntityId?: string;
    folderScopeId?: string;
    /**
     * FRONT-2522: the agent this chat is pinned to (direct agent chat). Rides
     * EVERY turn while set (standing, never consumed) — the server pins the
     * session on the first turn that names it and its stored pin is the truth
     * afterwards, so a resend is a no-op there. Deliberately absent from the resume
     * transport: a re-attach reads an already-running turn, it sends nothing.
     */
    directAgentId?: string;
    /**
     * FRONT-2195: whether this instance re-attaches to a still-running turn it
     * finds in `/history` (the pursuit/re-arm machinery below). Defaults to
     * `true`. Pass `false` ONLY for an instance whose messages no surface
     * renders — today the app-level `ChatProvider` (chat-context.tsx). Every
     * re-attaching instance runs its OWN resume connection (the pursuit
     * registry is keyed per instance), so a headless one would only add a
     * redundant connection for a chat nobody sees, and its replays would
     * re-claim and release the run's FRONT-1717 history write on every
     * attach — churn with no one on screen to benefit.
     */
    reattach?: boolean;
    /**
     * FRONT-2594: the Canvas item the person has open, read AT SEND TIME (a getter,
     * never a value captured at mount: they can switch items between messages).
     * Rides every turn the body function builds — a normal send, a retry, a
     * queued or continuation send all go through it — as `body.activeArtifact`;
     * null / absent leaves the field out entirely.
     */
    getActiveArtifact?: () => { kind: 'document' | 'deck' | 'sketch'; id: string } | null;
    /**
     * FRONT-2669: true where the Canvas panel exists for this chat (flag on, the
     * chat window), read at send time. Rides every turn as `body.canvasPanel: true`
     * so the server knows a panel is there to show a document beside the chat.
     * Absent (never `false`) everywhere else — the phone surface passes nothing.
     */
    getCanvasPanel?: () => boolean;
  },
) {
  const { sessionId: globalSessionId } = useSession();
  const sessionId = sessionIdOverride ?? globalSessionId;
  const reattachEnabled = opts?.reattach !== false;
  const agentId = 'customAgent';
  const queryClient = useQueryClient();

  // Optional per-turn context (FRONT-198): the inbox chat modal passes the
  // viewed item's content here. Held in a ref so the memoized transport's
  // body() reads the latest value without rebuilding the transport. The
  // backend injects it as a non-persisted system note (only when present),
  // so the global chat — which never sets this — is unaffected.
  const assetContextRef = useRef<string | undefined>(opts?.assetContext);
  useEffect(() => {
    assetContextRef.current = opts?.assetContext;
  }, [opts?.assetContext]);

  // FRONT-1739: alongside `assetContext`, name the exact item EDDY may
  // rewrite in place (see editable-asset.ts for how the modal derives this
  // — the SAME decision that gates the reader's own pencil affordance).
  // Same ref pattern as `assetContextRef` — read fresh on every send without
  // rebuilding the memoized transport.
  const assetEditableRef = useRef<AssetEditablePayload | undefined>(opts?.assetEditable);
  useEffect(() => {
    assetEditableRef.current = opts?.assetEditable;
  }, [opts?.assetEditable]);

  // FRONT-1191: the Work container (a project) this chat is about, set
  // once when a chat is minted from the board's/detail page's "Ask Eddy about
  // this" action. Held in a ref exactly like assetContext above — the
  // transport body reads the latest value without rebuilding the memoized
  // transport. The backend persists it once on the session row and injects a
  // "this conversation is about X" instruction into the prompt every turn
  // (src/agents/chat-proxy.ts), so it only needs to ride the FIRST turn here,
  // but sending it on every turn is harmless (the backend no-ops once it's
  // already persisted).
  const scopeEntityIdRef = useRef<string | undefined>(opts?.scopeEntityId);
  useEffect(() => {
    scopeEntityIdRef.current = opts?.scopeEntityId;
  }, [opts?.scopeEntityId]);

  // FRONT-776: the Folder this chat is scoped to, set once when a chat is
  // minted from the folder detail page's "Ask Eddy about this folder"
  // button. Same ride-only-the-first-turn-but-harmless-every-turn contract
  // as `scopeEntityIdRef` above — the backend persists it once on the
  // session row (`chat_sessions.scopeFolderId`) and re-injects the folder's
  // full contents into the prompt every turn from that persisted value
  // (src/agents/chat-proxy.ts), so resending it here is a no-op once set.
  const folderScopeIdRef = useRef<string | undefined>(opts?.folderScopeId);
  useEffect(() => {
    folderScopeIdRef.current = opts?.folderScopeId;
  }, [opts?.folderScopeId]);

  // FRONT-2522: same ref-in-body pattern as the two scope refs above.
  const directAgentIdRef = useRef<string | undefined>(opts?.directAgentId);
  useEffect(() => {
    directAgentIdRef.current = opts?.directAgentId;
  }, [opts?.directAgentId]);

  // FRONT-2594: always the latest getter, so the body function reads the item open NOW.
  const getActiveArtifactRef = useRef(opts?.getActiveArtifact);
  getActiveArtifactRef.current = opts?.getActiveArtifact;
  const getCanvasPanelRef = useRef(opts?.getCanvasPanel);
  getCanvasPanelRef.current = opts?.getCanvasPanel;

  // Files currently "in this chat's context" (FRONT-357): the inbox modal
  // keeps uploaded files riding along on EVERY turn (until their chip is
  // removed) so answers stay grounded in them. Ref + setter rather than an
  // option because the list is derived from this hook's own messages — the
  // caller computes it after the hook returns. The backend injects these
  // like normal attachments but never persists them on the user row.
  const contextAttachmentsRef = useRef<AttachmentRef[]>([]);
  const setContextAttachments = useCallback((files: AttachmentRef[]) => {
    contextAttachmentsRef.current = files;
  }, []);

  // Active branch + one-shot fork pointer for the next turn. The transport
  // body is composed from these via a ref so we don't have to rebuild the
  // DefaultChatTransport on every state change. `branchedFromMessageId` is
  // only set right after a retry so the server can stamp the back-pointer
  // on the cloned user message; cleared after the next turn fires.
  const [activeBranchId, setActiveBranchId] = useState<string>('main');
  const turnStateRef = useRef<BranchTurnState>({ branchId: 'main' });
  // Mirror activeBranchId into a ref so `onFinish` (which captures the
  // initial closure) always reads the current branch on each stream.
  const activeBranchIdRef = useRef('main');
  useEffect(() => {
    // Preserve any in-flight fork pointer when the branch changes —
    // retryMessage calls setActiveBranchId(fork.branchId) and then sets
    // turnStateRef.current = { branchId, branchedFromMessageId }; if
    // this effect overwrites the ref with just { branchId }, the
    // transport body() that fires moments later loses the fork pointer
    // and the server inserts the cloned user row with no
    // branchedFromMessageId. That breaks both context preservation and
    // the branch graph's anchor matching.
    const inflight = turnStateRef.current.branchedFromMessageId;
    turnStateRef.current = inflight
      ? { branchId: activeBranchId, branchedFromMessageId: inflight }
      : { branchId: activeBranchId };
    activeBranchIdRef.current = activeBranchId;
  }, [activeBranchId]);

  // ── Mid-run resume (FRONT-638, kept live indefinitely by FRONT-2195) ──────
  // When a chat is reopened while its last turn is still running on the server,
  // /history hands back a trailing assistant row at status="streaming" carrying
  // its `streamId`. We re-attach to that live turn by driving the AI SDK's
  // native resumeStream() at the existing resume endpoint, so tokens + tool
  // cards repaint live instead of the chat sitting silent on a frozen partial —
  // for as long as the server reports the turn running, across ceiling closes,
  // dropped connections and failed reconnects.
  //
  // All of that sequencing lives in `ResumePursuitController`
  // (resume-pursuit-controller.ts), one per hook instance, so it runs under
  // unit tests with fake timers; this hook only wires React state, the AI SDK
  // chat and the history query into its `deps` (assigned below, every
  // render) and forwards events to it: history changes (`considerHistory`),
  // stream errors (`onStreamError`), a deliberate Stop (`stop`, `suppress`),
  // mount/unmount and session changes. Give-up state is read by the bubbles
  // straight from resume-retry-policy.ts's module registry (FRONT-773
  // defect 1), which the controller keys per instance (FRONT-2195 review F6).
  const pursuitRef = useRef<ResumePursuitController<UIMessage> | null>(null);
  if (pursuitRef.current == null) {
    pursuitRef.current = new ResumePursuitController<UIMessage>({
      setMessages: () => {},
      resumeStream: async () => {},
      refetchHistory: async () => [],
      latestHistory: () => undefined,
      isHistoryFetching: () => false,
      localStatus: () => 'ready',
      onPursuitArmed: () => {},
    });
  }
  const pursuit = pursuitRef.current;

  // FRONT-765: the live turn's background-chat runId, delivered as a
  // transient `data-background-chat-run` chunk right at turn start (see
  // `onData` below). Read at Stop-press time by `stopRun` — a ref, not
  // state, since nothing needs to re-render off it. `useBackgroundChatForSession`'s
  // 2s poll is the fallback for the brief window before this chunk lands.
  //
  // Code-review finding: this hook instance can outlive a single session —
  // pwa-shell.tsx calls `useAgentChat(sessionId)` and switches sessions via
  // `setSessionId` on the SAME mounted instance (no remount), so a stale
  // runId from the PREVIOUS session would otherwise survive the switch and
  // let Stop cancel a different conversation's still-running turn. Reset on
  // every session change so a Stop press before the new session's own chunk
  // has arrived falls through to `fallbackRunId` (or null) instead.
  const liveBgChatRunIdRef = useRef<string | null>(null);
  useEffect(() => {
    // FRONT-1717 (code-review finding): give up the outgoing run's
    // history-write claim before losing its id. This hook instance can
    // outlive the session (see above), and the AI SDK routes `onFinish`
    // through a ref refreshed every render — so the orphaned stream's
    // `onFinish` writes the NEW session's history key and never the old
    // one's. Left claimed, the old session's settle would defer to a local
    // write that never lands there, and a surface still observing that key
    // without remounting would never see the answer.
    releaseLocalHistoryWrite(liveBgChatRunIdRef.current);
    liveBgChatRunIdRef.current = null;
  }, [sessionId]);
  const cancelBackgroundChat = useCancelBackgroundChat();

  // FRONT-765 (live-test finding, 2026-09-02): whether the CURRENT turn is a
  // deliberately-stopped one the composer must treat as over, independent of
  // `chat.status` and independent of the backend's own settle time. A round
  // already mid-flight (e.g. two parallel `delegate_to_agent` calls) cannot
  // be aborted server-side — the backend cancel only takes effect at the
  // next round boundary, which can be tens of seconds away — but the user
  // pressing Stop must free the composer NOW, not once that round-trip
  // catches up. State (not a ref) because composers derive their visible
  // "busy" flag from it and must re-render. Set by `stopRun`, cleared by
  // `sendMessageResettingLiveRun` (the same choke point that resets
  // `liveBgChatRunIdRef` for a brand new turn) and on session change.
  //
  // Deliberately does NOT stop the abandoned round from finishing server-side
  // or from being persisted — that turn's eventual answer is swallowed from
  // view, not aborted (Thread 2 / the follow-up ticket owns hiding it; today
  // it still reseeds into view once /history reflects it, same as before).
  const [manuallyStopped, setManuallyStopped] = useState(false);
  useEffect(() => {
    setManuallyStopped(false);
  }, [sessionId]);

  // FRONT-765 (live-test finding, 2026-08-31): set by `stopRun` right before
  // `chat.stop()`, read-and-cleared by `onFinish` below. Needed because the
  // AI SDK calls `onFinish({ isAbort: true, ... })` on ANY abort — including
  // a deliberate Stop press — and this hook's own onFinish reacts by
  // refetching /history. If the backend turn hasn't yet honored the
  // cooperative round-boundary cancel (still mid-round, e.g. an in-flight
  // delegate_to_agent call), that fresh /history snapshot still shows the
  // trailing row as `streamStatus: "interrupted"` with a live `streamId` —
  // indistinguishable, to the FRONT-638 resume-detection effect further
  // down, from "a dropped connection to reattach to". Left unguarded, Stop
  // triggers that effect to GET /resume?after=0 on the SAME still-running
  // stream: it replays the whole turn's tape on top of what's already
  // rendered (duplicated tool cards and text) and flips the composer back
  // to "Stop" mid-replay — from the user's perspective, Stop looks like it
  // did nothing (or made things worse), even on a turn the backend is about
  // to correctly cancel.
  const deliberateStopRef = useRef(false);

  // Always address history by an explicit branch — including "main". The old
  // code omitted `?branch=` for main, which made the server fall back to its
  // "most-recently-written branch" default and hand back a fork's history
  // under the "main" key. That's the root of FRONT-258: the client thought it
  // was on main while showing a fork, and the branch selector mislabelled it.
  const historyPath = useCallback(
    (branch: string) =>
      `/api/v2/chat/${sessionId}/history?branch=${encodeURIComponent(branch)}`,
    [sessionId],
  );

  const {
    data: history,
    isLoading: historyLoading,
    isFetching,
    // FRONT-2185 review finding 5: exposed so a caller with no other error
    // surface (the operator panel's Chat tab) can render one instead of a
    // blank chat indistinguishable from "no messages yet" — `historyLoading`
    // alone doesn't carry this: it's false the moment the query settles,
    // success or failure alike.
    isError: historyError,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: chatKeys.history(sessionId, activeBranchId),
    queryFn: async () => {
      const data = await api.get<UIMessage[]>(historyPath(activeBranchId));
      return Array.isArray(data) ? data : [];
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  // FRONT-773 defect 2: latest history snapshot, refreshed on every render
  // (not in an effect — must be current before the pursuit controller can
  // ever read it). The controller's retry and re-arm steps fire from timers
  // and stream callbacks that can be many seconds old — a closed-over
  // `history` there would restore or strip a stale snapshot. Its own
  // refetches write here too (`deps.refetchHistory`), so a decision made
  // right after one sees it before React has re-rendered.
  const historyRef = useRef<UIMessage[] | undefined>(history);
  historyRef.current = history;

  const desktopAcceptance = useRef(createDesktopChatAcceptance());

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `/api/v2/chat/${agentId}/stream`,
        // FRONT-1507: the reader's own device timezone, so the server can
        // convert timestamps into their local clock. A function (not a
        // plain object) so it's re-evaluated per request, matching `body`
        // below.
        headers: () => timezoneHeader(),
        body: async () => {
          const state = turnStateRef.current;
          const body: Record<string, unknown> = {
            memory: { thread: sessionId, resource: sessionId },
            branchId: state.branchId,
          };
          if (state.branchedFromMessageId) {
            body.branchedFromMessageId = state.branchedFromMessageId;
            // Consume — fork pointer applies to the immediately following
            // turn only. Subsequent messages stay on the same branch but
            // shouldn't claim to be forks.
            turnStateRef.current = { branchId: state.branchId };
          }
          if (state.attachments && state.attachments.length > 0) {
            body.attachments = state.attachments;
            // Consume — attachments apply to the next turn only.
            turnStateRef.current = { branchId: state.branchId };
          }
          if (state.queuedSend) {
            body.queuedSend = true;
            // Consume — single-turn queued-send marker (FRONT-2555).
            turnStateRef.current = { branchId: state.branchId };
          }
          if (state.inlineOnly) {
            body.inlineOnly = true;
            // Consume — single-turn inline-only directive (FRONT-201).
            turnStateRef.current = { branchId: state.branchId };
          }
          if (state.source) {
            body.source = state.source;
            // Consume — single-turn button-origin marker (FRONT-199).
            turnStateRef.current = { branchId: state.branchId };
          }
          if (state.saveReplyToDocument) {
            body.saveReplyToDocument = state.saveReplyToDocument;
            // Consume — single-turn server-side document save (FRONT-221).
            turnStateRef.current = { branchId: state.branchId };
          }
          if (state.updateDocument) {
            body.updateDocument = true;
            // Consume — single-turn "Update document" directive (FRONT-269).
            turnStateRef.current = { branchId: state.branchId };
          }
          if (state.writeToGraph) {
            body.writeToGraph = true;
            // Consume — single-turn "Save to Graph" directive (FRONT-269).
            turnStateRef.current = { branchId: state.branchId };
          }
          if (state.imageIntent || state.imageModel) {
            if (state.imageIntent) body.imageIntent = true;
            if (state.imageModel) body.imageModel = state.imageModel;
            // Consume — single-turn image button + model pick (FRONT-535).
            turnStateRef.current = { branchId: state.branchId };
          }
          if (assetContextRef.current) {
            body.assetContext = assetContextRef.current;
          }
          if (assetEditableRef.current) {
            body.assetEditable = assetEditableRef.current;
          }
          if (scopeEntityIdRef.current) {
            body.scopeEntityId = scopeEntityIdRef.current;
          }
          if (folderScopeIdRef.current) {
            body.folderScopeId = folderScopeIdRef.current;
          }
          if (directAgentIdRef.current) {
            body.directAgentId = directAgentIdRef.current;
          }
          const activeArtifact = getActiveArtifactRef.current?.() ?? null;
          if (activeArtifact) {
            // FRONT-2594/2600: standing, not consumed — re-read on every send.
            body.activeArtifact = activeArtifact;
          }
          if (getCanvasPanelRef.current?.() === true) {
            // FRONT-2669: standing, not consumed — re-read on every send.
            body.canvasPanel = true;
          }
          if (contextAttachmentsRef.current.length > 0) {
            body.contextAttachments = contextAttachmentsRef.current;
          }
          // FRONT-576/FRONT-820: ride the composer's quick-switch mode along
          // on every turn (sticky, not consumed) so "Fast" / "Thoughtful"
          // stays active for subsequent messages in THIS session until the
          // user changes it. `ensureChatModelMode` returns the cached value
          // immediately once it exists (the composer pill's optimistic write,
          // or a settled GET, already populated it — the common case) and
          // otherwise awaits the fetch, so the very first send right after a
          // reload can never race an empty cache slot and silently drop a
          // persisted mode (a pr-panel review caught this before merge).
          //
          // FRONT-1398 code review finding 9: this lookup is OPTIONAL — the
          // turn works fine with no mode at all (falls back to the normal
          // chat model) — so a failure here must never abort the send
          // itself. Previously unguarded, this awaited with no try/catch,
          // meaning any non-404 failure (a network blip, a 500) threw out of
          // the request-body builder and killed the user's message.
          //
          // FRONT-2522: NOT for a direct agent chat — Fast/Thoughtful picks
          // Eddy's orchestrator model and a pinned agent runs on its own. The
          // composer hides the switch there; this keeps a stale mode picked
          // earlier in the same session (before it was pinned) off the wire.
          //
          // FRONT-1698: with the composer model picker on, the pick replaces
          // the pills and no mode is sent (`resolveTurnModelMode`).
          const modelMode = await resolveTurnModelMode(queryClient, sessionId, !!directAgentIdRef.current);
          if (modelMode) body.modelMode = modelMode;
          return body;
        },
        // FRONT-638: point the AI SDK's reconnect (resumeStream) at our durable
        // resume endpoint for the streamId we're currently re-attaching to. The
        // streamId rides a ref (not the chat id the SDK passes) because the
        // resume route is keyed per-turn, not per-session. `after=0` replays the
        // whole turn from the tape (tokens + tool cards), then tails if it's
        // still live. credentials:'include' carries the auth cookie, same as the
        // live POST. When no resume is armed this is never invoked.
        prepareReconnectToStreamRequest: () => {
          const targetStreamId = pursuit.resumeTargetStreamId;
          return {
            api: targetStreamId
              ? `/api/v2/chat/stream/${encodeURIComponent(targetStreamId)}/resume?after=0`
              : `/api/v2/chat/${agentId}/stream`,
            credentials: 'include',
          };
        },
        // FRONT-2195 review F4/F5: the resume request is the only one the
        // pursuit controller needs a hand in — it carries the attempt's abort
        // signal (Stop cuts a re-attached stream at once; the SDK's own
        // stop() only flags an abort it never passes to this request), and
        // its HTTP status is recorded so a refusal (4xx) ends the pursuit
        // instead of retrying forever. The SDK surfaces neither. Every other
        // request passes straight through.
        fetch: async (input, init) => {
          const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
          if (!isResumeRequestUrl(url)) return desktopAcceptance.current.fetch(input, init);
          const signal = pursuit.resumeSignal();
          const response = await globalThis.fetch(input, signal ? { ...init, signal } : init);
          pursuit.noteResumeResponse(response.status);
          return response;
        },
      }),
    [agentId, sessionId],
  );

  // FRONT-3211: the chat status as of the last render, for `stopRun` (a stable callback).
  const chatStatusRef = useRef<string>('ready');
  const chat = useChat({
    // Keep the chat instance stable across branch switches — keying it by
    // activeBranchId remounts a fresh empty Chat on retry, throwing away
    // the prefix we set right before send. Branch switches drive messages
    // explicitly via setMessages instead.
    id: `chat-${sessionId}`,
    transport,
    // After every stream completes, refresh the persisted history so each
    // in-memory message gets its real database id (the retry endpoint takes
    // a numeric chat_messages.id, not the AI-SDK's synthetic one).
    //
    // FRONT-1685: this used to ALSO call `chat.setMessages(next)` directly,
    // right after writing `next` into the history query cache one line
    // above. That write is exactly what the reseed effect (below) watches —
    // it reacts and does its OWN `chat.setMessages(history)` for the SAME
    // data, independently, and the two calls raced: whichever fired second
    // was a redundant, no-op-in-substance second reconciliation, but it
    // still replaced the whole message array a second time, remounting
    // every message's subtree (any interactive card's local draft included)
    // twice in a row at the end of every single turn. The reseed effect
    // already fully covers this case (same `!isFetching && !isStreaming`
    // guard), so the fix is simpler: don't do the swap here at all. The
    // cache write and the invalidations still belong here; only the
    // redundant direct `setMessages` was removed.
    onFinish: async ({ isAbort }) => {
      // FRONT-765 (live-test finding): read-and-clear so a Stop press that
      // turned out to be a no-op (chat.stop() itself no-ops when nothing was
      // streaming) can't leave this poisoning a LATER, unrelated turn.
      const wasDeliberateStop = deliberateStopRef.current;
      deliberateStopRef.current = false;
      // FRONT-1698: the composer's model button reads the server's answer to
      // "which model will the next turn use" — re-read it now that a turn ran.
      refreshActiveChatModel(queryClient, sessionId);
      try {
        const data = await api.get<UIMessage[]>(historyPath(activeBranchIdRef.current));
        const next = Array.isArray(data) ? data : [];
        queryClient.setQueryData(chatKeys.history(sessionId, activeBranchIdRef.current), next);
        // FRONT-765 (live-test finding): poison the resume target in the
        // PERMANENT suppression set the resume-detection effect below
        // checks, so a deliberate Stop is never mistaken for a FRONT-638
        // "reopened mid-turn" reattach target — including on a later re-arm
        // attempt (FRONT-2195), not just the first one. See
        // resumeTargetToSuppress's own docblock for the full mechanism.
        const toSuppress = resumeTargetToSuppress({ isAbort, wasDeliberateStop, history: next });
        if (toSuppress) pursuit.suppress(toSuppress);
        // FRONT-2195 review F1: a stream that ended with the turn still
        // running (a re-attach tail that closed, a dropped connection, a Stop
        // whose cancel lands a round later) did not write the final answer —
        // give the claim back so this run's settle refetches, and refetch
        // once more if that settle already came and went during the fetch
        // above. See `handOffAfterStreamEnd`.
        if (handOffAfterStreamEnd(liveBgChatRunIdRef.current, next) === 'settled-meanwhile') {
          queryClient.invalidateQueries({
            queryKey: chatKeys.history(sessionId, activeBranchIdRef.current),
          });
        }
        // Refresh the branches list so the fork-nav picks up the new
        // branch this stream just created (if any).
        queryClient.invalidateQueries({ queryKey: ['chat', 'branches', sessionId] });
        queryClient.invalidateQueries({ queryKey: ['chat', 'messages-graph', sessionId] });
        // Refresh the plan query so the floating PlanCard picks up any
        // propose_plan / approve_plan / complete_plan that ran inside this
        // stream. The plan-changed eventBus SSE also fires, but it's
        // unreliable across tsx-watch restarts in dev — invalidating here
        // ties the refetch directly to the chat stream that created the
        // plan, which is the network call we actually trust.
        queryClient.invalidateQueries({ queryKey: planKeys.all });
        // FRONT-1075 / FRONT-1804: a brand-new chat's thread row is minted
        // server-side on this first reply and named ~3 s after Send; the
        // `chat-title-changed` push reaches this tab only on the replica that
        // named it, and the window polls its own row while its turn runs. This
        // end-of-stream refresh surfaces the row (and any title/recency change
        // on existing chats) in the lists immediately instead of waiting for
        // their 15s poll (use-chats.ts).
        queryClient.invalidateQueries({ queryKey: chatKeys.threadsRoot() });
        // FRONT-2546: a folder-scoped turn moves its folder to the front of
        // the Files folder strip, and Eddy's folder tool can file or remove
        // files mid-turn — neither goes through this client's own folder
        // mutations, so nothing else would refresh the strip or membership.
        queryClient.invalidateQueries({ queryKey: folderKeys.all });
      } catch (err) {
        // History refresh is best-effort — the in-memory chat still works
        // with the SDK's synthetic ids, only the retry button is held back.
        console.warn('[useAgentChat] post-stream history refresh failed:', err);
        // FRONT-1717: the fetch threw, so `setQueryData` above never ran and
        // this turn produced NO local write. Invalidate instead, so the
        // promotion still lands. FRONT-2195 review F1: nobody knows whether
        // this stream delivered the final state, so the claim is given back
        // too (fail open) — if the turn was still running, its later settle
        // must refetch; if it was final, that costs one extra refetch.
        handOffAfterStreamEnd(liveBgChatRunIdRef.current, null);
        queryClient.invalidateQueries({
          queryKey: chatKeys.history(sessionId, activeBranchIdRef.current),
        });
      }
    },
    // FRONT-638: a failed resume reconnect (e.g. a stale/pruned stream handle)
    // must not nag — the /history reseed already shows the interrupted partial
    // as the correct fallback. We only log; real send errors still surface via
    // chat.error wherever the UI reads it.
    // FRONT-773: also feed a resume-in-flight failure into the retry policy —
    // the pursuit controller only acts on it while one of its reconnect
    // attempts is actually in flight, so a normal send error (no resume in
    // progress) is a no-op there.
    onError: (err) => {
      console.warn('[useAgentChat] stream error:', err);
      pursuit.onStreamError();
      // FRONT-1717: deliberately does NOT release this run's local
      // history-write claim. The AI SDK calls `onFinish` from a `finally`
      // (`ai@6.0.174`, Chat.makeRequest) after a mid-stream error too, and
      // its own fetched /history decides there (`handOffAfterStreamEnd`,
      // FRONT-2195) whether its write was the final one. An earlier revision
      // released here unconditionally; that put the churn back on every
      // errored turn that had in fact finished (local write, then the
      // settle's invalidation).
    },
    // FRONT-765: capture the live turn's background-chat runId the moment it
    // arrives. `transient: true` on the chunk means this is the ONLY place it
    // surfaces — it's never appended to the message's rendered parts.
    //
    // PR-panel finding: this closure is recreated fresh on every render (not
    // wrapped in useCallback), and the AI SDK's `useChat` looks up the
    // LATEST one at call time via a per-call-site ref, even for a chunk
    // delivered by an OLD, orphaned Chat instance from a session we've since
    // switched away from (pwa-shell.tsx keeps one useAgentChat instance
    // mounted across session switches, with no abort of the previous
    // stream). So `sessionId` here is always the CURRENT session, and
    // comparing it against the chunk's own `sessionId` is what discards a
    // late chunk from a session the hook has moved on from — the earlier
    // session-change reset alone only guarded a STATIC leftover value, not
    // this DYNAMIC late-arrival case.
    onData: (dataPart) => {
      const signal = extractBackgroundChatRunSignal(dataPart);
      if (signal && signal.sessionId === sessionId) {
        liveBgChatRunIdRef.current = signal.runId;
        // FRONT-1717: this tab is streaming this run, so its `onFinish`
        // below owns the post-stream `chatKeys.history` write. Claim it here
        // — at turn start (or at attach time for a re-attach replay: the
        // chunk is on the tape), before the run can possibly settle — so the
        // SSE `background-chat-changed` settle handler defers instead of
        // firing a second, racing write to the same key. `onFinish` gives it
        // back when its stream ends before the turn does (FRONT-2195).
        claimLocalHistoryWrite(signal.runId);
      }
    },
  });

  // FRONT-2195: the pursuit controller's view of this hook, refreshed on
  // every render (like historyRef above) — its timers and stream callbacks
  // fire outside any render and must read the CURRENT chat, snapshot and
  // status, never the ones from when they were scheduled.
  chatStatusRef.current = chat.status;
  pursuit.deps = {
    setMessages: (messages) => chat.setMessages(messages),
    resumeStream: () => chat.resumeStream(),
    refetchHistory: async () => {
      const branch = activeBranchIdRef.current;
      const data = await api.get<UIMessage[]>(historyPath(branch));
      const next = Array.isArray(data) ? data : [];
      queryClient.setQueryData(chatKeys.history(sessionId, branch), next);
      historyRef.current = next;
      return next;
    },
    latestHistory: () => historyRef.current,
    isHistoryFetching: () => isFetching,
    localStatus: () => chat.status,
    // A turn the person did not stop is being attached — the composer must
    // offer Stop for it even if an EARLIER turn's Stop left the flag set
    // (FRONT-2195 review F5; a stopped turn is suppressed and never armed).
    onPursuitArmed: () => setManuallyStopped(false),
  };

  /**
   * FRONT-765 (PR-panel finding): the ONLY choke point every turn-starting
   * call in this hook must go through, instead of `chat.sendMessage`
   * directly — nulls `liveBgChatRunIdRef` the instant a NEW turn is
   * dispatched, before the network round trip. Without this, the ref still
   * holds the PREVIOUS turn's (already-completed) runId until the new
   * turn's own chunk lands; a Stop pressed in that window would cancel the
   * stale, already-settled run instead of the one actually streaming —
   * reopening the same bug this whole change exists to fix, one turn later,
   * in the SAME session (the session-change reset doesn't cover this case;
   * it's a different turn, not a different session).
   */
  const sendMessageResettingLiveRun = useCallback(
    (...args: Parameters<typeof chat.sendMessage>) => {
      // FRONT-765: a brand new turn is definitionally not a stopped one —
      // clear the composer's "user gave up on this" flag here too, the same
      // choke point that resets the runId ref, so a fresh send after a Stop
      // (manual or via the queued-draft auto-fire) correctly shows Stop
      // again instead of staying frozen on the previous turn's verdict.
      setManuallyStopped(false);
      liveBgChatRunIdRef.current = null;
      return chat.sendMessage(...args);
    },
    [chat.sendMessage],
  );

  // The pursuit controller's lifecycle. Declared BEFORE the detection effect
  // below so that, on StrictMode's mount/unmount/mount, the controller is
  // mounted again before `considerHistory` runs (see `unmount` there for why
  // its teardown waits one task).
  useEffect(() => {
    pursuit.mount();
    return () => pursuit.unmount();
  }, [pursuit]);

  // FRONT-2594: true once THIS session's history (on the ACTIVE branch) has finished loading AND the
  // reconcile below has run on it, i.e. the message list already reflects it.
  // `historyLoading` alone is not that signal: a cached history settles it
  // before the list is seeded (and a refetch settles it before the list is
  // brought up to date), so a consumer that treats "history is loaded" as
  // "messages are in place" sees the seeded messages as brand new.
  // Scoped to (session, branch): a branch switch swaps the whole list, so the
  // new branch's history must be seeded before the signal reads true again.
  const seededScope = `${sessionId}\u0000${activeBranchId}`;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  const historySeeded = seededFor === seededScope;

  // Keep the on-screen list reconciled with the server's authoritative
  // history. The old version only seeded when the list was EMPTY, so any
  // server append after the first render (voice-call turns, background
  // results — anything not from the user's own typed message) never painted
  // until a full page reload. reconcileHistory re-seeds whenever the
  // server is ahead/divergent, while refusing to (a) seed mid-refetch, (b)
  // clobber a live stream, or (c) revert a freshly streamed turn before its
  // post-stream history refetch lands. See reconcile-history.ts for the rules.
  useEffect(() => {
    // FRONT-638: stand down entirely while a mid-run resume is being armed/run.
    // Between stripping the trailing partial and the SDK flipping status to
    // "submitted" (the resume GET is awaited first), the displayed list is
    // shorter than /history, which would otherwise trip the "server is ahead"
    // reseed and re-add the partial we just stripped. Once the SDK is streaming,
    // the isStreaming guard below takes over; the pursuit clears it on settle.
    if (!history || pursuit.isResuming) return;
    const isStreaming = chat.status === 'streaming' || chat.status === 'submitted';
    const outcome = reconcileHistory(chat.messages, history, {
      isFetching,
      isStreaming,
      stoppedLocally: manuallyStopped,
    });
    if (outcome.action === 'apply') {
      chat.setMessages(outcome.messages);
    }
    // FRONT-2594: batched with the `setMessages` above, so the render that
    // reports `historySeeded` is the same one that carries the seeded list.
    if (!isFetching) setSeededFor(seededScope);
  }, [history, isFetching, chat.status, chat.setMessages, chat.messages.length, manuallyStopped, seededScope]);

  // FRONT-2530: when a tool call finishes mid-stream and changed chats on the
  // server (Eddy's `chat_sessions_delete`), refresh the chat lists NOW rather
  // than at the end of the stream — `chatQueryKeysForToolResult` names the
  // prefixes. Each tool call is handled once (`toolRefreshSeenRef`); parts
  // already finished when this instance first sees them (reopened history) are
  // marked seen without invalidating, and an instance nothing renders
  // (`reattachEnabled` false) skips it so a hidden instance cannot duplicate
  // the visible one's refetch. The end-of-stream `threadsRoot()` invalidation
  // in `onFinish` remains the backstop.
  const toolRefreshSeenRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    const isLive = chat.status === 'streaming' || chat.status === 'submitted';
    const seen = toolRefreshSeenRef.current ?? (toolRefreshSeenRef.current = new Set<string>());
    for (const message of chat.messages) {
      for (const part of message.parts ?? []) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const p = part as any;
        if (typeof p?.type !== 'string' || (!p.type.startsWith('tool-') && p.type !== 'dynamic-tool')) continue;
        if (p.state !== 'output-available' || !p.toolCallId || seen.has(p.toolCallId)) continue;
        seen.add(p.toolCallId);
        if (!isLive || !reattachEnabled) continue;
        const name: string = p.toolName ?? p.type.replace('tool-', '');
        for (const queryKey of chatQueryKeysForToolResult(name, p.output)) {
          queryClient.invalidateQueries({ queryKey: [...queryKey] });
        }
      }
    }
  }, [chat.messages, chat.status, reattachEnabled, queryClient]);

  // FRONT-3036: a live turn in this chat just ENDED here (streaming/submitted ->
  // idle). If a late approve from this tab is still waiting for the server's
  // continuation (the server starts it only after any live turn has ended),
  // the pickup re-reads run again. `reattach: false` (an unrendered instance)
  // opts out so a hidden instance cannot duplicate the visible one.
  const wasLiveRef = useRef(false);
  useEffect(() => {
    const isLive = chat.status === 'streaming' || chat.status === 'submitted';
    const ended = turnEndedHere(wasLiveRef.current, chat.status);
    wasLiveRef.current = isLive;
    if (ended && reattachEnabled) rerunPickupOnTurnEnd(sessionId, { queryClient });
  }, [chat.status, sessionId, reattachEnabled, queryClient]);

  // FRONT-638 / FRONT-773 / FRONT-2195: detect a reopened (or dropped-and-
  // reconnectable) still-running turn and re-attach to it. Runs when
  // /history or the local status changes (the reseed effect above seeds it
  // first); the controller decides — first attach at once, a re-arm after a
  // clean settle only while the turn reads live, spaced and re-checked
  // against a refetched /history. `reattach: false` (an instance nothing
  // renders) never pursues: this is the one call that can start a pursuit.
  useEffect(() => {
    pursuit.considerHistory(history, { sessionId, reattachEnabled, isFetching, localStatus: chat.status });
  }, [pursuit, sessionId, history, isFetching, chat.status, reattachEnabled]);

  // Explicit reload: fetch fresh history from the server and replace the
  // in-memory chat. Used after compress/clear actions where we WANT to
  // overwrite local state.
  const reload = useCallback(async () => {
    const data = await api.get<UIMessage[]>(historyPath(activeBranchId));
    const next = Array.isArray(data) ? data : [];
    queryClient.setQueryData(chatKeys.history(sessionId, activeBranchId), next);
    chat.setMessages(next);
  }, [sessionId, activeBranchId, chat.setMessages, queryClient, historyPath]);

  // Retry a user message: ask the server to allocate a fresh branch +
  // surface the source content + back-pointer, swap our active branch,
  // and re-send the user message so the chat-proxy writes it onto the
  // new branch and streams a new assistant reply.
  //
  // Accepts either the numeric `chat_messages.id` (when the message
  // came from /history) OR the message content (when the AI SDK still
  // holds onto its synthetic id post-stream). In the content path we
  // look the row up in /history first so the server only ever sees
  // numeric ids.
  const retryMessage = useCallback(
    async (input: { id?: string | number; content?: string }) => {
      let idNum =
        typeof input.id === 'string'
          ? parseInt(input.id, 10)
          : typeof input.id === 'number'
            ? input.id
            : NaN;

      // Fallback path — match the latest persisted user row by content
      // when we don't have a numeric id (or the SDK gave us a UUID).
      if (!Number.isFinite(idNum) && input.content) {
        try {
          const history = await api.get<Array<{ id?: string | number; role?: string; parts?: Array<{ type?: string; text?: string }> }>>(historyPath(activeBranchIdRef.current));
          // Walk from newest to oldest — the most recent matching user
          // message is what the user just clicked retry on.
          for (let i = history.length - 1; i >= 0; i--) {
            const m = history[i];
            if (m.role !== 'user') continue;
            const text = Array.isArray(m.parts)
              ? m.parts.filter((p) => p.type === 'text').map((p) => p.text ?? '').join('')
              : '';
            if (text === input.content) {
              const v = typeof m.id === 'string' ? parseInt(m.id, 10) : (m.id as number);
              if (Number.isFinite(v)) { idNum = v as number; break; }
            }
          }
        } catch (err) {
          console.warn('[retryMessage] content-lookup failed:', err);
        }
      }

      if (!Number.isFinite(idNum)) {
        toast.error('Cannot retry — message not yet persisted. Try again in a moment.');
        return;
      }
      try {
        const fork = await api.post<RetryResponse>(`/api/v2/chat/${sessionId}/retry/${idNum}`);
        // Compute the truncation point BEFORE switching branch — slice the
        // current in-memory thread at the retried user message so everything
        // before it stays visible. Match by numeric id first; fall back to
        // newest content match for synthetic-id messages.
        const current = chat.messages;
        let cutIdx = -1;
        for (let i = current.length - 1; i >= 0; i--) {
          const m = current[i];
          const mid = typeof m.id === 'string' ? parseInt(m.id, 10) : (m.id as unknown as number);
          if (Number.isFinite(mid) && mid === idNum) { cutIdx = i; break; }
        }
        if (cutIdx === -1 && input.content) {
          for (let i = current.length - 1; i >= 0; i--) {
            const m = current[i];
            if (m.role !== 'user') continue;
            const text = (m.parts ?? [])
              .filter((p) => p.type === 'text')
              .map((p) => (p as { text?: string }).text ?? '')
              .join('');
            if (text === input.content) { cutIdx = i; break; }
          }
        }
        const prefix = cutIdx >= 0 ? current.slice(0, cutIdx) : [];

        // Switch branch BEFORE sending so the history query and transport
        // body pick up the new id together. Pin it as an explicit choice so
        // the load-time adoption effect doesn't override the fork (FRONT-258).
        userPickedBranchRef.current = true;
        setActiveBranchId(fork.branchId);
        turnStateRef.current = {
          branchId: fork.branchId,
          branchedFromMessageId: fork.branchedFromMessageId,
        };
        chat.setMessages(prefix);
        // Optimistically refetch branches so the nav appears immediately
        // on the retried message (rather than waiting for onFinish).
        queryClient.invalidateQueries({ queryKey: ['chat', 'branches', sessionId] });
        queryClient.invalidateQueries({ queryKey: ['chat', 'messages-graph', sessionId] });
        await sendMessageResettingLiveRun({ text: fork.content });
      } catch (err) {
        toast.error((err as Error).message || 'Retry failed');
      }
    },
    [sessionId, chat.setMessages, sendMessageResettingLiveRun],
  );

  // Switch the active branch — fetches the new branch's parent-walked
  // history first, then swaps the in-memory chat in one step. Without
  // this two-phase swap, the old `setMessages([])` opened a window
  // where (a) the branch graph's alt-dot anchors had no row to attach
  // to so dots fell to the top, and (b) anything the user typed during
  // the wait went out with no context — the LLM saw the bare prompt
  // and replied "what do you mean?".
  const switchBranch = useCallback(
    async (branchId: string) => {
      if (branchId === activeBranchId) return;
      // Explicit user choice — pin it so the load-time adoption effect won't
      // yank us back to the server's most-recent-activity default.
      userPickedBranchRef.current = true;
      try {
        const data = await api.get<UIMessage[]>(historyPath(branchId));
        const next = Array.isArray(data) ? data : [];
        queryClient.setQueryData(chatKeys.history(sessionId, branchId), next);
        setActiveBranchId(branchId);
        chat.setMessages(next);
      } catch (err) {
        // Fall back to the old behaviour rather than getting stuck —
        // the seeding effect will re-hydrate from the next /history
        // response when the query refetches.
        console.warn('[switchBranch] history fetch failed:', err);
        setActiveBranchId(branchId);
        chat.setMessages([]);
      }
    },
    [activeBranchId, chat.setMessages, sessionId, queryClient],
  );

  /**
   * Delete a persisted message. Same id-or-content shape as
   * `retryMessage` — falls back to a /history role-and-content lookup
   * when the in-memory message still has the AI SDK's synthetic id.
   */
  const deleteMessage = useCallback(
    async (input: { id?: string | number; role?: string; content?: string }) => {
      let idNum =
        typeof input.id === 'string'
          ? parseInt(input.id, 10)
          : typeof input.id === 'number'
            ? input.id
            : NaN;

      if (!Number.isFinite(idNum) && input.content) {
        try {
          const history = await api.get<Array<{ id?: string | number; role?: string; parts?: Array<{ type?: string; text?: string }> }>>(historyPath(activeBranchIdRef.current));
          for (let i = history.length - 1; i >= 0; i--) {
            const m = history[i];
            if (input.role && m.role !== input.role) continue;
            const text = Array.isArray(m.parts)
              ? m.parts.filter((p) => p.type === 'text').map((p) => p.text ?? '').join('')
              : '';
            if (text === input.content) {
              const v = typeof m.id === 'string' ? parseInt(m.id, 10) : (m.id as number);
              if (Number.isFinite(v)) { idNum = v as number; break; }
            }
          }
        } catch (err) {
          console.warn('[deleteMessage] content-lookup failed:', err);
        }
      }
      if (!Number.isFinite(idNum)) {
        toast.error('Cannot delete — message not yet persisted.');
        return;
      }
      try {
        await api.delete<{ deleted: boolean }>(`/api/v2/chat/${sessionId}/messages/${idNum}`);
        toast.success('Message deleted');
        // Refresh history + in-memory chat so the row disappears.
        const data = await api.get<UIMessage[]>(historyPath(activeBranchIdRef.current));
        const next = Array.isArray(data) ? data : [];
        queryClient.setQueryData(chatKeys.history(sessionId, activeBranchIdRef.current), next);
        chat.setMessages(next);
      } catch (err) {
        toast.error((err as Error).message || 'Delete failed');
      }
    },
    [sessionId, chat.setMessages, queryClient],
  );

  // Delete a non-main branch. If the active branch is being deleted,
  // fall back to main so the chat surface doesn't end up pointed at a
  // branch that no longer exists.
  const deleteBranch = useCallback(
    async (branchId: string) => {
      if (branchId === 'main') {
        toast.error('Cannot delete the main branch.');
        return;
      }
      try {
        await api.delete<{ deleted: number; branchId: string }>(
          `/api/v2/chat/${sessionId}/branches/${encodeURIComponent(branchId)}`,
        );
        toast.success('Branch deleted');
        if (activeBranchIdRef.current === branchId) {
          setActiveBranchId('main');
          chat.setMessages([]);
        }
        queryClient.invalidateQueries({ queryKey: ['chat', 'branches', sessionId] });
        queryClient.invalidateQueries({ queryKey: chatKeys.history(sessionId, branchId) });
      } catch (err) {
        toast.error((err as Error).message || 'Delete branch failed');
      }
    },
    [sessionId, chat.setMessages, queryClient],
  );

  // Merge a non-main branch into its parent: the server asks the LLM to
  // summarize the branch's messages, posts that summary as a single
  // assistant turn on the parent branch, then drops the merged branch.
  // We jump the user to the parent so the new summary message is visible
  // immediately.
  const [mergingBranchId, setMergingBranchId] = useState<string | null>(null);
  const mergeBranch = useCallback(
    async (branchId: string) => {
      if (branchId === 'main') {
        toast.error('Cannot merge the main branch — it has no parent.');
        return;
      }
      if (mergingBranchId) return;
      setMergingBranchId(branchId);
      const toastId = toast.loading('Merging branch — summarizing messages…');
      try {
        const result = await api.post<{
          merged: boolean;
          parentBranchId: string;
          mergedCount: number;
        }>(
          `/api/v2/chat/${sessionId}/branches/${encodeURIComponent(branchId)}/merge`,
          {},
        );
        const parent = result.parentBranchId || 'main';
        toast.success(
          `Merged ${result.mergedCount} message${result.mergedCount === 1 ? '' : 's'} into ${parent === 'main' ? 'main' : parent.slice(0, 10) + '…'}`,
          { id: toastId },
        );
        const path =
          parent === 'main'
            ? `/api/v2/chat/${sessionId}/history`
            : `/api/v2/chat/${sessionId}/history?branch=${encodeURIComponent(parent)}`;
        const next = await api.get<UIMessage[]>(path);
        const list = Array.isArray(next) ? next : [];
        queryClient.setQueryData(chatKeys.history(sessionId, parent), list);
        setActiveBranchId(parent);
        chat.setMessages(list);
        queryClient.invalidateQueries({ queryKey: ['chat', 'branches', sessionId] });
        queryClient.invalidateQueries({ queryKey: chatKeys.history(sessionId, branchId) });
      } catch (err) {
        toast.error((err as Error).message || 'Merge branch failed', { id: toastId });
      } finally {
        setMergingBranchId(null);
      }
    },
    [sessionId, chat.setMessages, queryClient, mergingBranchId],
  );

  // Branches + per-anchor fork-group lookup powers the `‹ k / N ›` nav
  // we render on retried user messages.
  const { data: branchData } = useBranches(sessionId);
  const branches = branchData?.branches ?? [];
  const forkGroups = useMemo(
    () => buildForkGroups(branches),
    [branches],
  );

  // FRONT-258: the server's resolved active branch is the single source of
  // truth for what should be on screen. It picks the most-recently-active
  // branch in this session; the client used to ignore it and assume "main",
  // so the UI showed a fork's history under the "main" label and the switcher
  // couldn't escape. Adopt it on load, but never override an explicit user
  // choice — switchBranch / retryMessage set this ref.
  const userPickedBranchRef = useRef(false);
  // New conversation → forget the previous session's selection so the next
  // server resolution applies (the old code never reset this, so switching
  // conversations could carry a stale branch id across).
  useEffect(() => {
    userPickedBranchRef.current = false;
    setActiveBranchId('main');
    // FRONT-638: a different session is a different turn — the pursuit
    // controller drops the old session's resume state itself, the moment it
    // sees the new session id (`considerHistory`), so that reset can never
    // land AFTER a pursuit for the new session has started.
  }, [sessionId]);
  useEffect(() => {
    setActiveBranchId((cur) =>
      nextActiveBranch(cur, branchData?.activeBranchId, userPickedBranchRef.current),
    );
  }, [branchData?.activeBranchId]);

  // Wrapper around the AI SDK's sendMessage that routes optional file
  // attachments through the turn-state ref so the transport body picks
  // them up on the immediately following request (and clears them after).
  // Attachments are also passed as `metadata.attachments` on the AI SDK
  // call so the optimistic user message renders chips immediately, before
  // the stream finishes and /history is reloaded.
  const sendMessage = useCallback(
    (
      opts: { text: string } & ChatSendOptions,
      attachments?: AttachmentRef[],
      image?: { intent?: boolean; model?: string },
      desktopSignal?: AbortSignal,
    ) => {
      const patch: Partial<BranchTurnState> = {};
      if (opts.queuedSend) patch.queuedSend = true;
      if (attachments && attachments.length > 0) patch.attachments = attachments;
      if (image?.intent) patch.imageIntent = true;
      if (image?.model) patch.imageModel = image.model;
      if (Object.keys(patch).length > 0) {
        turnStateRef.current = { ...turnStateRef.current, ...patch };
      }
      const dispatch = (headers?: Record<string, string>) => sendMessageResettingLiveRun(
        attachments && attachments.length > 0
          ? { text: opts.text, metadata: { attachments } as Record<string, unknown> }
          : { text: opts.text },
        headers ? { headers } : undefined,
      );
      if (!desktopSignal) return dispatch();
      return desktopAcceptance.current.send(dispatch, desktopSignal);
    },
    [sendMessageResettingLiveRun],
  );

  /**
   * Send a message that must run inline in THIS chat (FRONT-201). Used by
   * the inbox modal's resolve buttons: flags the next turn inline-only so
   * the chat-proxy keeps the work in the modal's own stream (propose_plan
   * is refused that turn) instead of punting it to a background plan that
   * surfaces in the main chat. One-shot — consumed by the transport body.
   */
  const sendInline = useCallback(
    (opts: {
      text: string;
      source?: MessageSource;
      saveReplyToDocument?: string;
      // FRONT-269: "Update document" button. Sets the deterministic directive
      // so the server owns the rewrite instruction (no injected English prompt).
      updateDocument?: boolean;
    }) => {
      turnStateRef.current = {
        ...turnStateRef.current,
        inlineOnly: true,
        ...(opts.source ? { source: opts.source } : {}),
        ...(opts.saveReplyToDocument ? { saveReplyToDocument: opts.saveReplyToDocument } : {}),
        ...(opts.updateDocument ? { updateDocument: true } : {}),
      };
      // Pass source as metadata too (FRONT-199) so the optimistic user bubble
      // shows the friendly status chip immediately — without it the raw prompt
      // would flash until the post-stream /history reload lands. Same trick
      // `sendMessage` uses for attachments.
      return sendMessageResettingLiveRun(
        opts.source
          ? { text: opts.text, metadata: { source: opts.source } as Record<string, unknown> }
          : { text: opts.text },
      );
    },
    [sendMessageResettingLiveRun],
  );

  /**
   * Send a button-triggered message that should run through the normal
   * plan/preview gate (NOT inline). Used by the inbox "Save to Graph" button
   * (FRONT-230): it carries the friendly source chip like sendInline, but does
   * NOT set inlineOnly — so the orchestrator is free to propose_plan and show
   * the entity preview before anything is written, instead of writing blind.
   */
  const sendGatedAction = useCallback(
    (opts: {
      text: string;
      source?: MessageSource;
      // FRONT-269: "Save to Graph" button. Sets the deterministic directive so
      // the server triggers the write-to-graph preview gate directly, instead
      // of relying on a typed /write-to-graph slash command in the text.
      writeToGraph?: boolean;
    }) => {
      if (opts.source || opts.writeToGraph) {
        turnStateRef.current = {
          ...turnStateRef.current,
          ...(opts.source ? { source: opts.source } : {}),
          ...(opts.writeToGraph ? { writeToGraph: true } : {}),
        };
      }
      return sendMessageResettingLiveRun(
        opts.source
          ? { text: opts.text, metadata: { source: opts.source } as Record<string, unknown> }
          : { text: opts.text },
      );
    },
    [sendMessageResettingLiveRun],
  );

  /**
   * FRONT-765: stop the composer's Stop button actually meant. The AI SDK's
   * own `stop()` only detaches the local SSE read — the server-side turn
   * (FRONT-204 background continuation) keeps running to completion
   * regardless, burning tokens the user explicitly tried to cancel. This
   * additionally cancels the turn's `background_chats` row, which the
   * chat-proxy tool-round loop already polls cooperatively between rounds
   * (`isBackgroundChatCancelled`) — so a multi-round/delegation turn stops at
   * its next round boundary instead of running to completion.
   *
   * `fallbackRunId` is the session's polled background-chat row (already in
   * scope at each call site via `useBackgroundChatForSession`) — used only
   * for the brief window before the live SSE-delivered runId has arrived.
   * No live run to cancel (nothing ever opened, or it already settled) is a
   * harmless no-op: the mutation's own 404 handling covers that race.
   */
  const stopRun = useCallback(
    (fallbackRunId?: string | null) => {
      // FRONT-2195 review F5: poison the turn on screen and cancel any
      // pending reconnect or re-arm NOW. `onFinish` below poisons too, but
      // only when a stream was open; a Stop pressed while a reconnect was
      // backing off, or during a re-arm gap, had no stream to finish, and the
      // pending timer re-attached to the turn just stopped.
      // FRONT-3211: is a turn of this chat actually on screen in this tab
      // (its own stream, or a re-attach in progress)? A turn the server
      // started (a chat task's result turn) runs before this tab attaches to
      // it. Read before `pursuit.stop()` clears the re-attach state.
      const status = chatStatusRef.current;
      const attached = status === 'streaming' || status === 'submitted' || pursuit.isResuming;
      pursuit.stop();
      // FRONT-1728: send the cancel BEFORE closing the stream. Closing it
      // aborts the model call, and a plain streaming answer then reaches the
      // server's final write within milliseconds — a cancel sent after the
      // close routinely lost that race and the reply was saved as finished.
      // FRONT-3211: the live runId is only this tab's CURRENT turn while a
      // stream is attached. Unattached it still names the last turn this tab
      // streamed (long finished), and cancelling that stopped nothing: the
      // polled running row is the turn on the server.
      const runId = resolveStopRunId({
        liveRunId: attached ? liveBgChatRunIdRef.current : null,
        fallbackRunId,
      });
      if (runId) cancelBackgroundChat.mutate({ runId });
      // FRONT-765 (live-test finding): flag this abort as deliberate BEFORE
      // calling stop() — onFinish (fired synchronously inside the SDK's
      // stop→abort→finally chain) reads it to tell this apart from a
      // dropped-connection reattach case. See the ref's own docblock above.
      deliberateStopRef.current = true;
      chat.stop();
      // FRONT-1728: say so on the reply right away. The server marks its row
      // stopped too, but nothing refetches this chat once it has.
      // FRONT-3211: only when that reply is on screen. Unattached, the last
      // assistant message here is an EARLIER, finished reply: marking it
      // would say "you stopped this" about the wrong answer. The server
      // stamps the turn it stopped, and the next history read shows it.
      if (attached) chat.setMessages((messages) => markStoppedTail(messages));
      // FRONT-765 (live-test finding, 2026-09-02): free the composer NOW.
      // A round already mid-flight (e.g. two parallel delegate_to_agent
      // calls) has no server-side abort — the cooperative cancel only takes
      // effect at the NEXT round boundary, which can be a minute away — so
      // waiting for chat.status or the polled background_chats row to
      // settle before unlocking the composer left Stop looking broken: the
      // button stayed on "Stop", inert, until the abandoned round finished
      // on its own. The user's intent is decided the instant they click,
      // not once the network catches up.
      setManuallyStopped(true);
    },
    [chat.stop, chat.setMessages, cancelBackgroundChat, pursuit],
  );

  return {
    ...chat,
    sendMessage,
    sendInline,
    sendGatedAction,
    stopRun,
    manuallyStopped,
    /** FRONT-3211: the run id of the last turn this tab streamed (its own, or one it re-attached to). */
    lastStreamedRunId: () => liveBgChatRunIdRef.current,
    historyLoading,
    historySeeded,
    historyError,
    refetchHistory,
    reload,
    retryMessage,
    deleteMessage,
    switchBranch,
    activeBranchId,
    branches: branches ?? [],
    forkGroups,
    deleteBranch,
    mergeBranch,
    mergingBranchId,
    setContextAttachments,
  };
}
